// src/components/layouts/admindashboard/header/sub/MessageButton.tsx

import React, { useState, useRef, useEffect } from "react";
import { MessageSquareText, Mail, Clock, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  message: string;
  createdAt: string;
  read: boolean;
}

const initialMessages: ContactMessage[] = [
  {
    id: 1,
    name: "Sophie Martin",
    email: "sophie.martin@example.com",
    message: "Bonjour, je suis intéressée par vos services...",
    createdAt: "2025-04-18T10:30:00",
    read: false,
  },
  {
    id: 2,
    name: "Thomas Bernard",
    email: "thomas.bernard@example.com",
    message: "Merci pour votre devis. Je souhaiterais discuter des délais.",
    createdAt: "2025-04-17T15:20:00",
    read: false,
  },
  {
    id: 3,
    name: "Julie Dubois",
    email: "julie.dubois@example.com",
    message: "Félicitations pour votre travail, j'aimerais collaborer.",
    createdAt: "2025-04-16T09:45:00",
    read: true,
  },
];

const MessageButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ContactMessage[]>(initialMessages);
  const menuRef = useRef<HTMLDivElement>(null);
  const unreadCount = messages.filter((m) => !m.read).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = (id: number) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, read: true } : msg))
    );
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays === 1) return "Hier";
    return `Il y a ${diffDays} jours`;
  };

  const truncateMessage = (msg: string, maxLen = 50) => {
    if (msg.length <= maxLen) return msg;
    return msg.substring(0, maxLen) + "...";
  };

  return (
    <div className="sm:relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-black/20 text-white border border-white/30 backdrop-blur-sm hover:bg-accent hover:text-black hover:scale-105 transition-all duration-200 shadow-md"
      >
        <MessageSquareText size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-accent text-black rounded-full ring-2 ring-white/30 animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-5 sm:right-0 mt-2 w-80 rounded-2xl bg-black/95 backdrop-blur-md border border-accent/30 shadow-2xl overflow-hidden z-50"
          >
            <div className="px-4 py-3 border-b border-accent/20 bg-gradient-to-r from-secondary/20 to-transparent">
              <h3 className="text-sm font-semibold text-white">Messages de contact</h3>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  Aucun message
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`relative px-4 py-3 border-b border-white/10 transition-all duration-150 hover:bg-white/5 cursor-pointer ${
                      !msg.read ? "bg-accent/5" : ""
                    }`}
                    onClick={() => markAsRead(msg.id)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                          <User size={14} className="text-accent" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-white truncate">
                            {msg.name}
                          </p>
                          <p className="text-[10px] text-gray-400 flex items-center gap-1 flex-shrink-0">
                            <Clock size={10} />
                            {formatRelativeTime(msg.createdAt)}
                          </p>
                        </div>
                        <p className="text-xs text-gray-300 truncate mt-0.5">
                          {truncateMessage(msg.message)}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <Mail size={10} className="text-gray-500" />
                          <span className="text-[10px] text-gray-400 truncate">
                            {msg.email}
                          </span>
                        </div>
                      </div>
                      {!msg.read && (
                        <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0 shadow-[0_0_4px_#FBC02D]" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="px-4 py-2 border-t border-accent/20 text-center">
              <a
                href="/admin/dashboard/messages"
                className="text-xs text-accent hover:text-white transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Voir tous les messages
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageButton;